import { Job } from "@freeconvert/freeconvert-node/dist/types";
import { getSocket } from "client/utils/getSocket";
import { Socket } from "socket.io-client";

let socket: Socket | null = null;

let lastDisconnectTime: Date | null = null;
let offlineLimitWatcherInterval: NodeJS.Timer | null = null;
const OFFLINE_LIMIT = 60 * 1000 * 10; // 10 minutes

export interface SocketData<T> {
    id: string;
    job: T;
    time: number;
    user: string;
}

export interface JobSocketOptions {
    handleJobFail: (job: Job) => void;
    handleJobComplete: (job: Job) => void;
    handleReconnect?: () => void;
    handleDisconnect?: () => void;
    onOfflineLimitReach?: (job: Job) => void;
}

// for using only one socket for the whole upload
let isConnecting = false; // if socket is connecting
let hasConnected = false; // if socket is connected
/*
    A job will be added to pendingJobs when isConnected && !hasConnected.
    After socket connects all the pending jobs will subscribe 
    to job.${job.id} channel.
*/
let pendingJobs: { job: Job; options: JobSocketOptions }[] = [];
let jobs: { job: Job; options: JobSocketOptions }[] = [];

export class JobSocket {
    constructor(job: Job, options: JobSocketOptions) {
        jobs.push({ job, options });

        this.initSocket(job, options);
        this.setOfflineLimitWatcher();
        this.addInternetEventListeners();
        this.addCypressEventListener(job, options);
    }

    private async initSocket(job: Job, options: JobSocketOptions) {
        if (isConnecting && !hasConnected) {
            pendingJobs.push({ job: job, options });
            return;
        }

        if (socket) {
            this.subscribe(job);
            return;
        }

        isConnecting = true;
        socket = await getSocket();

        socket.connected && this.handleSocketConnect(socket, job);

        socket.on("connect", () => {
            if (!socket) return;
            this.handleSocketConnect(socket, job);
        });

        socket.on("disconnect", this.handleDisconnect);

        socket.on("reconnect", this.handleReconnect);
    }

    private handleSocketConnect(socket: Socket, job: Job) {
        isConnecting = false;
        hasConnected = true;
        this.subscribe(job);
        this.handlePendingTasksSubscribe();

        socket?.on("job_failed", (data: SocketData<Job>) => {
            this.handleJobFailed(data.job);
        });

        socket?.on("job_started", (data: any) => {});

        socket?.on("job_completed", (data: SocketData<Job>) => {
            this.handleJobComplete(data.job);
        });
    }

    private handlePendingTasksSubscribe = () => {
        pendingJobs.forEach((job) => {
            this.subscribe(job.job);
        });

        pendingJobs = [];
    };

    private handleJobComplete = (job: Job) => {
        jobs.forEach((_job) => {
            if (_job.job.id !== job.id) return;

            _job.options.handleJobComplete(job);
        });
    };

    private handleJobFailed = (job: Job) => {
        jobs.forEach((_job) => {
            if (_job.job.id !== job.id) return;

            _job.options.handleJobFail(job);
        });
    };

    private handleReconnect = async () => {
        jobs.forEach((job) => job.options.handleReconnect && job.options.handleReconnect());
        this.removeOfflineLimitWatcher();
    };

    private handleDisconnect = () => {
        lastDisconnectTime = new Date();
        this.setOfflineLimitWatcher();
        jobs.forEach((job) => job.options.handleDisconnect && job.options.handleDisconnect());
    };

    private subscribe(job: Job) {
        socket?.emit("subscribe", `job.${job.id}`);
    }

    private addInternetEventListeners() {
        window.addEventListener("online", this.handleReconnect);
        window.addEventListener("offline", this.handleDisconnect);
    }

    private addCypressEventListener(job: Job, options: JobSocketOptions) {
        // @ts-ignore
        if (window.Cypress) {
            document.body.addEventListener(`job_failed.${job.id}`, (data: any) => {
                options.handleJobFail(data.job as Job);
            });
        }
    }

    private setOfflineLimitWatcher() {
        if (offlineLimitWatcherInterval) return;

        offlineLimitWatcherInterval = setInterval(() => {
            if (!lastDisconnectTime) return;

            if (new Date().getTime() - lastDisconnectTime.getTime() >= OFFLINE_LIMIT) {
                this.removeOfflineLimitWatcher();
                jobs.forEach((job) => job.options.onOfflineLimitReach && job.options.onOfflineLimitReach(job.job));
            }
        }, 1000);
    }

    private removeOfflineLimitWatcher() {
        offlineLimitWatcherInterval && clearInterval(offlineLimitWatcherInterval);
    }
}
